import { CircularProgress } from '@material-ui/core';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { KeyValuePair } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import produce from 'immer';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KVPairInfo, BaseKVPairInfoProps, BaseKVPairInfoUpdatableInputs, capitalizeAll, BaseInfoCompProps } from '@moderepo/mode-app-components';
import { createHomeKVStore, userAppDataContext, selectHomeKVStore, updateHomeKVStore } from '@moderepo/mode-data-state-user-app';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { useSelectAllHomeKVStores } from '../../utils/dataStateHooks';


interface InputErrors {
    key: string | null;
    value: string | null;
}


interface HomeKVPairInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly kvPairKey?: string;                                    // will be undefined if this container is used for creating new KV Pair
    readonly newKVPairSettings?: {                                  // Only available if this container is used for creating new KV Pair
        readonly onNewKVPairCreated: ()=> void;
        readonly onNewKVPairCanceled: ()=> void;
    }

    // This container used KVPairInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;
}


/**
 * This container is for displaying a KVPairInfoComp and provide all the logic for a KVPairInfoComp e.g. fetching data and updating data.
 * Because we need to show KVPairInfoComp in multiple places, it is better to wrap the KVPairInfoComp UI into 1 container
 * and use this container in places where we want to show KVPairInfoComp
 */
export const HomeKVPairInfoContainer = (props: HomeKVPairInfoContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const kvPairs = useSelectAllHomeKVStores(props.homeId);
    const selectedKVPair = props.kvPairKey ? selectHomeKVStore(appDataState, props.homeId, props.kvPairKey) : undefined;
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        key: null, value: null,
    });
    const isNewKVPair = props.kvPairKey === undefined;



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.key is undefined which is when user want to create a KV Pair
     */
    useEffect(() => {
        if (isNewKVPair) {
            onChangeEditingMode(true);
        }
    }, [isNewKVPair, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            key: null, value: null,
        });

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onKVPairInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    /**
     * Create a New Key Value pair
     */
    const onCreateNewKVPair = useCallback(async (key: string | undefined, value: string | undefined) => {
        const newInputErrors: InputErrors = ({
            key: null, value: null,
        });

        if (!key) {
            newInputErrors.key = 'error.input.kv_pair_key.empty';
        } else if (key.includes(' ') || key.includes('\t')) {
            // make sure key does not contain spaces
            newInputErrors.key = 'error.input.kv_pair_key.invalid';
        }

        // make sure the new key is not already exist
        if (kvPairs && kvPairs.find((kvPair: KeyValuePair): boolean => {
            return kvPair.key === key;
        })) {
            newInputErrors.key = 'error.input.kv_pair_key.exist';
        }

        if (!value) {
            newInputErrors.value = 'error.input.kv_pair_value.empty';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.key && !newInputErrors.value && key && value) {
            try {
                // Input validated successfully. Try to format the value. If we can turn the value to JSON, turn it to json.
                // If not, save it as string
                const formattedValue = ((v: string): any => {
                    try {
                        // If successfully convert value to json, return value as json
                        return JSON.parse(v);
                    } catch (error) {
                        // JSON.parse failed, ignore error
                    }

                    // unable to convert value to JSON, return value as string
                    return v;
                })(value);

                await appDataDispatch(createHomeKVStore(props.homeId, key, formattedValue));
                closeEditing();
                uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'label.kv_pair' }));
                if (props.newKVPairSettings) {
                    props.newKVPairSettings.onNewKVPairCreated();
                }
            } catch (error) {
                // ignore error
            }
        }
    }, [kvPairs, appDataDispatch, props.homeId, props.newKVPairSettings, closeEditing, uiDispatch]);


    /**
     * Handler for when the user click Save input from the KVPair form.
     * If we are in creating new KV Pair mode, call createNewKVPair. Else update the current KV Pair
     */
    const onKVPairInfoFormSubmit = useCallback(async (inputs: BaseKVPairInfoUpdatableInputs) => {
        if (!project) {
            return;
        }

        const { key, value } = inputs;

        if (isNewKVPair) {
            onCreateNewKVPair(key, value);
        } else if (selectedKVPair) {

            const newInputErrors: InputErrors = ({
                key: null, value: null,
            });

            // Validate inputs
            if (!value) {
                newInputErrors.value = 'error.input.kv_pair_value.empty';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.value && key) {
                // Inputs validated successfully
                if (value && value !== selectedKVPair.value) {
                    try {
                        // Input validated successfully. Try to format the value. If we can turn the value to JSON, turn it to json.
                        // If not, save it as string
                        const formattedValue = ((v: string): any => {
                            try {
                                // If successfully convert value to json, return value as json
                                return JSON.parse(v);
                            } catch (error) {
                                // JSON.parse failed, ignore error
                            }

                            // unable to convert value to JSON, return value as string
                            return v;
                        })(value);

                        // dispatch an action to update the KV Pair's value
                        await appDataDispatch(updateHomeKVStore(props.homeId, selectedKVPair.key, formattedValue));
                        uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.kv_pair' }));
                        closeEditing();
                    } catch (error) {
                        /* ignore error */
                    }
                } else {
                    // newName is the same so no need to make api call
                    closeEditing();
                }
            }
        }
    }, [project, isNewKVPair, selectedKVPair, onCreateNewKVPair, appDataDispatch, props.homeId, uiDispatch, closeEditing]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newKVPairSettings !== undefined) {
            props.newKVPairSettings.onNewKVPairCanceled();
        }
    }, [props.newKVPairSettings, closeEditing]);



    /**
     * Create the input settings for the KVPairInfoComp and cache it
     */
    const inputsSettings = useMemo((): BaseKVPairInfoProps['fields'] => {

        const jsonValue = selectedKVPair?.value && typeof selectedKVPair?.value === 'object'
            ? JSON.stringify(selectedKVPair.value, null, 4)
            : String(selectedKVPair?.value || '');

        const defaultSettings: BaseKVPairInfoProps['fields'] = {
            value: {
                label       : capitalizeAll(trans('label.value')),
                displayValue: jsonValue,
                value       : jsonValue,
                error       : inputErrors.value ? trans(inputErrors.value) : '',
                editable    : true,
            },
        };

        if (!isNewKVPair && selectedKVPair) {
            // If key is specified then that mean we are showing KV Pair info for a specific KV Pair. Foe existing KV Pair, we can show
            // modified date
            return {
                ...defaultSettings,
                key: {
                    label       : capitalizeAll(trans('label.kv_pair_key')),
                    displayValue: selectedKVPair.key,
                    value       : selectedKVPair.key,
                    editable    : false,
                },
                modificationTime: {
                    label       : capitalizeAll(trans('label.updated_date')),
                    displayValue: getDateTimeAndElapsedTime(selectedKVPair.modificationTime, trans),
                    value       : selectedKVPair.modificationTime,
                },
            };
        }

        if (isNewKVPair) {
            // If Key is not specified, that mean this container is used for creating new KV Pair. We need to set Key input field to be editable
            return {
                ...defaultSettings,
                key: {
                    label       : capitalizeAll(trans('label.kv_pair_key')),
                    displayValue: selectedKVPair?.key || '',
                    value       : selectedKVPair?.key || '',
                    editable    : true,
                    error       : inputErrors.key ? trans(inputErrors.key) : '',
                },
            };
        }

        return defaultSettings;
    }, [isNewKVPair, selectedKVPair, trans, inputErrors.value, inputErrors.key]);


    return (
        <>
            {project && (selectedKVPair || isNewKVPair) && (
                <KVPairInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(isNewKVPair ? 'label.add_key_value_pair' : 'label.kv_pair_info'))}
                    icon="fas fa-database"
                    fields={inputsSettings}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewKVPair ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onKVPairInfoFormSubmit,
                        },
                        formatJson: {
                            label  : capitalizeAll(trans('label.format_json')),
                            onClick: () => {},
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onKVPairInfoFormInputChange}
                />
            )}

            {(!project || (!isNewKVPair && !selectedKVPair)) && (
                <CircularProgress />
            )}
        </>

    );
};
